import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { Client, ClientWritePayload } from '@/api/clients/clientsApi.types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/components/ui/fieldError';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { PhoneInput } from '@/components/ui/phoneInput';
import { Icon } from '@/components/common/Icon';
import { useCreateClientMutation, useUpdateClientMutation } from '@/hooks/useClientMutations';
import { capitalizeWordsAsYouType, formatPostalCodeAsYouType } from '@/utils/inputFormatting';
import { isBlank, isValidEmail, isValidPostalCode } from '@/utils/formValidation';
import { isValidPhoneNumber } from '@/utils/phoneNumbers';
import {
  SUPPORTED_LANGUAGE_OPTIONS,
  FALLBACK_LANGUAGE,
  isSupportedLanguage,
  type SupportedLanguage,
} from '@/i18n/supportedLanguages';
import { cn } from '@/lib/utils';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

const LANGUAGE_NAME_KEY: Record<SupportedLanguage, string> = {
  en: 'form.languageEnglish',
  pl: 'form.languagePolish',
};

interface ClientFormModalProps {
  client: Client | null;
  onClose: () => void;
}

function initialValue(client: Client | null, field: keyof Client): string {
  if (client === null) {
    return '';
  }
  const value = client[field];
  return typeof value === 'string' ? value : '';
}

function initialLanguage(client: Client | null, uiLanguage: SupportedLanguage): SupportedLanguage {
  if (
    client !== null &&
    client.preferredLanguage !== null &&
    isSupportedLanguage(client.preferredLanguage)
  ) {
    return client.preferredLanguage;
  }
  return uiLanguage;
}

export function ClientFormModal({ client, onClose }: ClientFormModalProps) {
  const { t, i18n } = useTranslation(['clients', 'common']);
  const isEditing = client !== null;
  const createMutation = useCreateClientMutation();
  const updateMutation = useUpdateClientMutation();

  const resolvedUiLanguage =
    i18n.resolvedLanguage === undefined ? i18n.language : i18n.resolvedLanguage;
  const uiLanguage = isSupportedLanguage(resolvedUiLanguage)
    ? resolvedUiLanguage
    : FALLBACK_LANGUAGE;

  const [fullName, setFullName] = useState(initialValue(client, 'fullName'));
  const [email, setEmail] = useState(initialValue(client, 'email'));
  const [phone, setPhone] = useState(initialValue(client, 'phone'));
  const [addressLine, setAddressLine] = useState(initialValue(client, 'addressLine'));
  const [city, setCity] = useState(initialValue(client, 'city'));
  const [postalCode, setPostalCode] = useState(initialValue(client, 'postalCode'));
  const [preferredLanguage, setPreferredLanguage] = useState<SupportedLanguage>(
    initialLanguage(client, uiLanguage)
  );
  const [showErrors, setShowErrors] = useState(false);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const nameError = showErrors === true && isBlank(fullName) ? 'validation:requiredField' : null;
  const emailError =
    showErrors === true && isBlank(email) === false && isValidEmail(email) === false
      ? 'validation:email'
      : null;
  const postalError =
    showErrors === true && isBlank(postalCode) === false && isValidPostalCode(postalCode) === false
      ? 'validation:postalCode'
      : null;

  function toNullable(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const hasError =
      isBlank(fullName) ||
      (isBlank(email) === false && isValidEmail(email) === false) ||
      (isBlank(phone) === false && isValidPhoneNumber(phone) === false) ||
      (isBlank(postalCode) === false && isValidPostalCode(postalCode) === false);
    if (hasError === true) {
      setShowErrors(true);
      return;
    }
    const payload: ClientWritePayload = {
      fullName: fullName.trim(),
      email: toNullable(email),
      phone: toNullable(phone),
      addressLine: toNullable(addressLine),
      city: toNullable(city),
      postalCode: toNullable(postalCode),
      country: null,
      preferredLanguage,
    };

    function handleError(saveError: unknown) {
      const backendMessage = resolveApiErrorMessage(saveError);
      showErrorNotification(
        backendMessage.length > 0 ? backendMessage : t('clients:form.saveError')
      );
    }

    function handleSuccess() {
      showSuccessNotification(t('clients:form.saved'));
      onClose();
    }

    if (isEditing === true && client !== null) {
      updateMutation.mutate(
        { clientId: client.id, payload },
        { onSuccess: handleSuccess, onError: handleError }
      );
      return;
    }
    createMutation.mutate(payload, { onSuccess: handleSuccess, onError: handleError });
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submit}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">
            {isEditing === true ? t('clients:form.editTitle') : t('clients:form.addTitle')}
          </h2>
          <button type="button" aria-label={t('common:actions.close')} onClick={onClose}>
            <Icon name="X" className="text-zoomuted" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="client-name">{t('clients:form.fullName')}</Label>
            <Input
              id="client-name"
              value={fullName}
              hasError={nameError !== null}
              placeholder={t('clients:form.fullNamePlaceholder')}
              onChange={function update(changeEvent) {
                setFullName(capitalizeWordsAsYouType(changeEvent.target.value));
              }}
            />
            <FieldError messageKey={nameError} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="client-email">{t('clients:form.email')}</Label>
              <Input
                id="client-email"
                type="email"
                value={email}
                hasError={emailError !== null}
                placeholder={t('clients:form.emailPlaceholder')}
                onChange={function update(changeEvent) {
                  setEmail(changeEvent.target.value.trim());
                }}
              />
              <FieldError messageKey={emailError} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="client-phone">{t('clients:form.phone')}</Label>
              <PhoneInput
                inputId="client-phone"
                value={phone}
                isValidationVisible={showErrors}
                placeholder={t('clients:form.phonePlaceholder')}
                onChange={setPhone}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="client-address">{t('clients:form.addressLine')}</Label>
            <Input
              id="client-address"
              value={addressLine}
              placeholder={t('clients:form.addressPlaceholder')}
              onChange={function update(changeEvent) {
                setAddressLine(changeEvent.target.value);
              }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="client-city">{t('clients:form.city')}</Label>
              <Input
                id="client-city"
                value={city}
                placeholder={t('clients:form.cityPlaceholder')}
                onChange={function update(changeEvent) {
                  setCity(capitalizeWordsAsYouType(changeEvent.target.value));
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="client-postal">{t('clients:form.postalCode')}</Label>
              <Input
                id="client-postal"
                inputMode="numeric"
                value={postalCode}
                hasError={postalError !== null}
                placeholder={t('clients:form.postalPlaceholder')}
                onChange={function update(changeEvent) {
                  setPostalCode(formatPostalCodeAsYouType(changeEvent.target.value));
                }}
              />
              <FieldError messageKey={postalError} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('clients:form.language')}</Label>
            <div className="inline-flex bg-zoooffwhite border border-zooborder rounded-xl p-1">
              {SUPPORTED_LANGUAGE_OPTIONS.map(function renderLanguageOption(language) {
                const isActive = language === preferredLanguage;
                return (
                  <button
                    key={language}
                    type="button"
                    aria-pressed={isActive}
                    onClick={function chooseLanguage() {
                      setPreferredLanguage(language);
                    }}
                    className={cn(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-zooprimary text-zoowhite shadow-sm'
                        : 'text-zoomuted hover:text-zooindigo'
                    )}
                  >
                    {t('clients:' + LANGUAGE_NAME_KEY[language])}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zoomuted">{t('clients:form.languageHint')}</p>
          </div>
          <p className="text-[11px] flex items-center gap-1.5 text-zoomuted">
            <Icon name="Info" className="text-zooprimary" />
            {t('clients:form.contactHint')}
          </p>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" isLoading={isPending}>
            {t('clients:form.save')}
          </Button>
        </div>
      </form>
    </ModalOverlay>
  );
}
